import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../../lib/utils";

type DialogSize = "sm" | "md" | "lg" | "xl" | "full";

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** @deprecated Use size="full" or size="lg" instead */
  fullscreenOnMobile?: boolean;
  /** Dialog size: sm (max-w-md), md (max-w-lg), lg (max-w-2xl), xl (max-w-4xl), full (fullscreen) */
  size?: DialogSize;
  className?: string;
}

const sizeClasses: Record<DialogSize, string> = {
  sm: "w-full max-w-md m-4",
  md: "w-full max-w-lg m-4",
  lg: "w-full max-w-2xl m-4",
  xl: "w-full max-w-4xl m-4",
  full: "w-full h-full rounded-none sm:w-[95vw] sm:h-[95vh] sm:max-w-6xl sm:rounded-lg",
};

export function Dialog({
  isOpen,
  onClose,
  title,
  children,
  fullscreenOnMobile = false,
  size,
  className,
}: DialogProps) {
  // Determine size class
  const getSizeClass = () => {
    if (size) return sizeClasses[size];
    if (fullscreenOnMobile) {
      return "w-full h-full rounded-none sm:w-auto sm:h-auto sm:max-w-2xl sm:rounded-lg sm:m-4";
    }
    return sizeClasses.md;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center"
        >
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50"
            onClick={onClose}
          />

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "relative bg-card shadow-xl max-h-[95vh] overflow-hidden flex flex-col border border-border",
              size !== "full" && "rounded-lg",
              getSizeClass(),
              className
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30 shrink-0">
              <h2 className="text-lg font-semibold">{title}</h2>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body - scrollable */}
            <div className="flex-1 overflow-y-auto">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
